import {
  Paper,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import classes from './ccf-directory.module.scss';
import { useEffect, useState } from 'react';
import {
  Agent,
  fetchAgentContacts,
  selectAgentList,
  selectDropDownQueryValue,
  selectLoadingStatus,
  selectSearchBoxQueryValue,
  selectSkillsValue,
} from './+state/ccf-directory.slice';
import { useDispatch, useSelector } from 'react-redux';
import { CcfDirectorySelect } from './ccf-directory-select/ccf-directory-select';
import { CcfDirectorySearch } from './ccf-directory-search/ccf-directory-search';
import DirectoryItem from './ccf-directory-item/ccf-directory-item';
import {
  CcfDirectoryIcon,
  CcfHeader,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import CcfOutboundOptions from '../ccf-outbound-options/ccf-outbound-options';
import CcfDirectoryItemContact from './ccf-directory-item-contact/ccf-directory-item-contact';
import { activeContactSelector } from '../ccf-inbox/ccf-inbox.slice';
import { styled } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@mui/material';

/* eslint-disable-next-line */
export interface CcfDirectoryProps {
  isFullView: boolean;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    directoryHeader: {
      background: `${theme.palette.background.level1} 0% 0% no-repeat padding-box`,
      borderRadius: '5px 5px 0px 0px',
      opacity: 1,
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '5px',
      marginTop: '0px !important',
      '& section': {
        marginBottom: '0px !important',
        alignItems: 'center',
      },
      '& > div': {
        marginTop: '0px !important',
      },
      '& label': {
        color: theme.palette.text.secondary,
        marginLeft: '4px !important',
        marginTop: '0px !important',
        fontWeight: 'bold',
        lineHeight: '1',
      },
      '& svg': {
        verticalAlign: 'middle',
      },
    },
  })
);

/**
 * Component for ccf directory
 * @param props - CcfDirectoryProps
 * @example - <CcfDirectory />
 * @returns
 */
export function CcfDirectory(props: CcfDirectoryProps) {
  const dispatch = useDispatch();
  const style = useStyles();
  const users = useSelector(selectAgentList);
  const elevatedContact = useSelector(activeContactSelector);
  const loading = useSelector(selectLoadingStatus);
  const [callTypeSelected, updateCallTypeSelcted] = useState(false);
  const searchBoxQuery = useSelector(selectSearchBoxQueryValue);
  const numberSkills = useSelector(selectSkillsValue);
  const [searchContact, updateSearchContact] = useState<string>('');
  const selectedDropDownValue = useSelector(selectDropDownQueryValue);

  useEffect(() => {
    if (users.length === 0 && !searchBoxQuery) {
      if (selectedDropDownValue !== 'people') {
        dispatch(fetchAgentContacts());
      }
    }
  }, []);

  useEffect(() => {
    //re rendering component
  }, [selectedDropDownValue]);

  /**
   * Function to update call type
   * @param trigger - boolean
   * @example - outboundCallOptionSelcted(true)
   */
  const outboundCallOptionSelcted = (trigger: boolean) => {
    updateCallTypeSelcted(trigger);
  };

  const LabeledSwitch = styled(Switch)(({ theme }) => ({
    width: 40,
    height: 22,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(18px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#1976d2',
          opacity: 1,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: 18,
      height: 18,
      boxShadow: 'none',
    },
    '& .MuiSwitch-track': {
      borderRadius: 20,
      backgroundColor: '#bdbdbd',
      opacity: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'baseline', // Aligns children to baseline
      fontSize: 12,
      fontWeight: 600,
      color: '#fff',
      '&::before, &::after': {
        position: 'absolute',
        bottom: 2, // nudges text toward the baseline
        fontSize: 9,
        width: '50%',
        textAlign: 'center',
        pointerEvents: 'none',
      },
      '&::before': {
        content: '"On"',
        left: 0,
      },
      '&::after': {
        content: '"Off"',
        right: 0,
      },
    },
  }));
  const [checked, setChecked] = useState(false);

  return (
    <Paper square elevation={0} classes={{ root: classes.directoryContainer }}>
      <div className={style.directoryHeader}>
        <CcfHeader
          LeftIcon={<CcfDirectoryIcon className={classes.directoryIcon} />}
          headerText={'Directory'}
          headerTextClassess={classes.headerText}
        ></CcfHeader>
      </div>
      <div
        style={{
          padding: '16px 8px 8px 3px',
          backgroundColor: 'rgb(248, 250, 251)',
        }}
      >
        <CcfDirectorySearch updateSearchQuery={updateSearchContact} />
      </div>
      <div
        style={{
          padding: '8px 8px 16px 3px',
          backgroundColor: 'rgb(248, 250, 251)',
        }}
      >
        <CcfDirectorySelect />
      </div>
      <div
        style={{
          padding: '8px 8px 16px 3px',
          backgroundColor: 'rgb(248, 250, 251)',
        }}
      >
        <FormControlLabel
          control={
            <LabeledSwitch
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
          }
          label="Include external directories"
          sx={{
            gap: 1.5,
            marginLeft: 1,
            alignItems: 'center',
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem',
            },
          }}
        />
      </div>
      <Paper
        square
        component={'ul'}
        classes={{ root: classes.directory }}
        elevation={0}
      >
        {!callTypeSelected ? (
          <>
            {!loading &&
              selectedDropDownValue !== 'people' &&
              users.map((user) => (
                <DirectoryItem
                  key={user.id}
                  user={user as Agent}
                  isHovering={false}
                />
              ))}
            {!loading && selectedDropDownValue === 'people' && (
              <CcfDirectoryItemContact
                elevatedContact={elevatedContact as any}
                isHovering={false}
              />
            )}
            {!loading && users.length === 0 && (
              <CcfTypography
                translationKey="noMatchesFound"
                classes={{ root: classes.noAgentFoundTypography }}
              />
            )}
            {loading && (
              <Box textAlign="center" mt={3} width="100%">
                <CircularProgress size={20} />
              </Box>
            )}
          </>
        ) : null}
        {!loading && users.length === 0 && (
          <CcfOutboundOptions
            selectCallType={outboundCallOptionSelcted}
            number={searchContact}
            skills={numberSkills?.[searchContact] || []}
          />
        )}
      </Paper>
    </Paper>
  );
}

export default CcfDirectory;
